import {
  Breadcrumbs,
  Theme,
  Typography,
  Grid,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker";
import KenColors from "../../Helpers/Themes/kenColors";
import { useAuthContext } from "../../Helpers/Contexts/authContext";
import { useCourseContext } from "../../Helpers/Contexts/courseContext";
import { getIdCard, getImageAsBase64 } from "../../Utils/apiService";
import KenLoader from "../../GlobalComponents/KenLoader";

interface IdCardData {
  previewUrl?: string;
  downloadUrl?: string;
  fileType?: string;
  title?: string;
}

const renderPdfPreviewImage = async (downloadUrl: string): Promise<string> => {
  const dataUrl = await getImageAsBase64(downloadUrl);
  const response = await fetch(dataUrl);
  const arrayBuffer = await response.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);

  const maxWidth = 560;
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = maxWidth / baseViewport.width;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to render PDF preview");
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: context, viewport }).promise;
  return canvas.toDataURL("image/png");
};

const parseIdCardResponse = (response: unknown): IdCardData | null => {
  if (!response || typeof response !== "object") {
    return null;
  }

  const root = response as Record<string, unknown>;
  if (root.success === false) {
    return null;
  }

  const data = root.data;
  const idCard =
    data && typeof data === "object" && (data as Record<string, unknown>).idCard
      ? ((data as Record<string, unknown>).idCard as Record<string, unknown>)
      : (root.idCard as Record<string, unknown> | undefined);

  if (!idCard || typeof idCard !== "object") {
    return null;
  }

  const previewUrl = idCard.previewUrl as string | undefined;
  const downloadUrl = idCard.downloadUrl as string | undefined;

  if (!previewUrl && !downloadUrl) {
    return null;
  }

  return {
    previewUrl,
    downloadUrl,
    fileType: (idCard.fileType as string | undefined) || "PDF",
    title: idCard.title as string | undefined,
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  mainText: {
    fontWeight: "600 !important",
    textDecoration: "none",
    textTransform: "capitalize",
    margin: "0px !important",
    padding: "0px !important",
    "@media (min-width: 1280px) and (max-width: 1300px)": {
      fontSize: "13px !important",
    },
    "@media (min-width: 1920px) and (max-width: 1940px)": {
      fontSize: "20px !important",
    },
  },
  listText: {
    textDecoration: "none",
    textTransform: "capitalize",
    margin: "0px !important",
    padding: "0px !important",
  },
  homeWelcomeRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  eIdTitle: {
    fontSize: "18px !important",
    fontWeight: 700,
    color: "#373A45",
  },
}));

const AppBreadCrumbs: React.FunctionComponent = () => {
  const { pathname, state } = useLocation();
  const { state: AuthState } = useAuthContext();
  const { user } = AuthState;
  const { value: courseState } = useCourseContext() || { value: { current: {} } };
  const theme = useTheme();
  const is1440 = useMediaQuery("@media (max-width: 1440px)");
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();

  const { enqueueSnackbar } = useSnackbar();
  const [eIdModalOpen, setEIdModalOpen] = useState(false);
  const [idCardLoading, setIdCardLoading] = useState(false);
  const [downloadingEId, setDownloadingEId] = useState(false);
  const [idCardData, setIdCardData] = useState<IdCardData | null>(null);
  const [idCardError, setIdCardError] = useState<string | null>(null);
  const [pdfPreviewImage, setPdfPreviewImage] = useState<string | null>(null);

  const affiliationId = courseState?.current?.Id as string | undefined;

  useEffect(() => {
    if (!eIdModalOpen) {
      setIdCardData(null);
      setIdCardError(null);
      setIdCardLoading(false);
      setPdfPreviewImage(null);
      return;
    }

    if (!affiliationId) {
      setIdCardError("Affiliation details not found. Please try again after your profile loads.");
      return;
    }

    const loadIdCard = async () => {
      setIdCardLoading(true);
      setIdCardError(null);
      try {
        const response = await getIdCard(affiliationId);
        const parsed = parseIdCardResponse(response);
        if (!parsed) {
          const message =
            (response as Record<string, unknown>)?.errorMessage ||
            (response as Record<string, unknown>)?.message ||
            "ID card is not available right now.";
          setIdCardError(String(message));
          return;
        }
        setIdCardData(parsed);

        if (parsed.downloadUrl && parsed.fileType?.toLowerCase() === "pdf") {
          const previewImage = await renderPdfPreviewImage(parsed.downloadUrl);
          setPdfPreviewImage(previewImage);
        }
      } catch (error: any) {
        console.error("Failed to load ID card:", error);
        setIdCardError(
          error?.response?.data?.errorMessage ||
            error?.response?.data?.message ||
            "Failed to load ID card. Please try again.",
        );
      } finally {
        setIdCardLoading(false);
      }
    };

    loadIdCard();
  }, [eIdModalOpen, affiliationId]);

  const handleDownloadEId = () => {
    if (!idCardData?.downloadUrl) {
      enqueueSnackbar("Download URL is not available.", { variant: "error" });
      return;
    }

    try {
      setDownloadingEId(true);
      const safeTitle =
        idCardData.title?.trim().replace(/[^a-zA-Z0-9()\s_-]+/g, "").replace(/\s+/g, " ") ||
        `E-ID-Card-${(user?.applicantName || "Student").trim().replace(/[^a-zA-Z0-9]+/g, "-")}`;
      const extension = idCardData.fileType?.toLowerCase() === "pdf" ? "pdf" : "file";

      const link = document.createElement("a");
      link.href = idCardData.downloadUrl;
      link.download = `${safeTitle}.${extension}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();

      enqueueSnackbar("E-ID card download started.", { variant: "success" });
    } catch (error) {
      console.error("E-ID download failed:", error);
      enqueueSnackbar("E-ID download failed. Please try again.", { variant: "error" });
    } finally {
      setDownloadingEId(false);
    }
  };

  const getCustomBreadcrumbs = (path: string): Array<{ label: string; path: string }> | null => {
    if (path === "/exams/results") {
      return [
        { label: "Exams", path: "/exams" },
        { label: "Results", path: "/exams/results" },
      ];
    }
    if (path === "/exams/marks-breakdown") {
      return [
        { label: "Exams", path: "/exams" },
        { label: "Results", path: "/exams/results" },
        { label: "Marks Breakdown", path: "/exams/marks-breakdown" },
      ];
    }
    if (path === "/exams/grade-cards-download") {
      return [
        { label: "Exams", path: "/exams" },
        { label: "Results", path: "/exams/results" },
        { label: "Grade Cards Download", path: "/exams/grade-cards-download" },
      ];
    }
    return null;
  };

  const customBreadcrumbs = getCustomBreadcrumbs(pathname);
  const navigateHistory: Array<string> = decodeURIComponent(pathname)
    .split("/")
    .filter((items) => {
      return items && items !== "";
    });
  const navigateState: null | string = state?.breadCrumbs;
  let customPath: string = "";

  const renderIdCardContent = () => {
    if (idCardLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 320 }}>
          <KenLoader />
        </Box>
      );
    }

    if (idCardError) {
      return (
        <Typography sx={{ color: "#8C8E94", fontSize: "14px", textAlign: "center", py: 4 }}>
          {idCardError}
        </Typography>
      );
    }

    if (pdfPreviewImage) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {idCardData?.title && (
            <Typography sx={{ color: "#585A64", fontSize: "14px", fontWeight: 500 }}>
              {idCardData.title}
            </Typography>
          )}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              borderRadius: "12px",
              border: "1px solid #EAEFFF",
              backgroundColor: "#fff",
              p: 1,
            }}
          >
            <Box
              component="img"
              src={pdfPreviewImage}
              alt={idCardData?.title || "E-ID Card"}
              sx={{
                width: "100%",
                maxWidth: 560,
                height: "auto",
                display: "block",
                borderRadius: "8px",
              }}
            />
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      {navigateHistory[0] === "home" ? (
        <Box className={classes.homeWelcomeRow}>
          <Typography variant={is1440 ? "mediumthin" : "largethin"} sx={{ mb: 0 }}>
            Welcome,{" "}
            <Typography variant={is1440 ? "mediumbold" : "largebold"}>{user?.applicantName}</Typography>
          </Typography>

          <Button variant="contained" size="large" onClick={() => setEIdModalOpen(true)}>
            View E-ID Card
          </Button>
        </Box>
      ) : (
        <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={"0.9375rem"}>
          {customBreadcrumbs ? "Exams" : navigateHistory[0]}
        </Typography>
      )}
      {navigateHistory[0] === "home"
        ? null
        : (customBreadcrumbs || navigateHistory.length > 1) && (
            <Grid style={isMobileOrTablet ? { margin: "3px 20px" } : {}}>
              <Breadcrumbs separator={<Typography sx={{ fontSize: "0.75rem", fontWeight: "500" }}>&gt;</Typography>} style={{ color: "#3061FF" }}>
                {customBreadcrumbs ? (
                  customBreadcrumbs.map((crumb, index) => {
                    const isLastIndex = index === customBreadcrumbs.length - 1;
                    return (
                      <Link key={index} className={classes.listText} to={crumb.path}>
                        <Typography sx={{ color: isLastIndex ? KenColors.ClientBlack2 : "#3061FF", fontWeight: isLastIndex ? "400" : "600", fontSize: "0.75rem" }}>{crumb.label}</Typography>
                      </Link>
                    );
                  })
                ) : (
                  navigateHistory.map((items, index) => {
                    customPath = customPath + `;${items}`;
                    const isLastIndex = index === navigateHistory.length - 1;
                    const newPath = customPath.replace(/;/g, "/");
                    let newItem = items.replace(/-/g, " ");
                    return index === navigateHistory.length - 1 && navigateState ? (
                      <Typography key={index} className={classes.listText} color={"#193389"} fontSize={"0.75rem"}>
                        {navigateState}
                      </Typography>
                    ) : (
                      <Link key={index} className={classes.listText} to={`${newPath}`}>
                        <Typography sx={{ color: isLastIndex ? KenColors.ClientBlack2 : "#3061FF", fontWeight: isLastIndex ? "400" : "600", fontSize: "0.75rem" }}>{newItem}</Typography>
                      </Link>
                    );
                  })
                )}
              </Breadcrumbs>
            </Grid>
          )}

      <Dialog open={eIdModalOpen} onClose={() => setEIdModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography className={classes.eIdTitle} sx={{ fontSize: "16px !important" }}>
            E-ID Card
          </Typography>
          <IconButton onClick={() => setEIdModalOpen(false)} size="small" sx={{ color: "#585A64" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>{renderIdCardContent()}</DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEIdModalOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadEId}
            disabled={downloadingEId || idCardLoading || !!idCardError || !idCardData?.downloadUrl || !pdfPreviewImage}
            sx={{
              textTransform: "none",
              backgroundColor: "#3061FF",
              "&:hover": { backgroundColor: "#2551E6" },
            }}
          >
            {downloadingEId ? "Downloading..." : "Download"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppBreadCrumbs;
